import React, { type FunctionComponent } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Empty, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";

import { chatActions } from "@fitness-app/app-store";
import { ChatStatus } from "@fitness-app/data-models/entities/Chat";

import { UploadFilesProvider } from "~/components/Upload/UploadProvider";
import { useIsPhone } from "~/hooks/useIsMobile";
import ChatChannelsList from "~/modules/Chat/ChatChannelsList";
import ChatWindow from "~/modules/Chat/ChatWindow";
import ChatMessageEditProvider from "~/modules/Chat/components/ChatMessageEditContext";
import { ChatUtilitiesProvider } from "~/modules/Chat/providers/ChatUtilitiesProvider";
import { getUploadConfig } from "~/modules/Chat/uploadConfig";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

const Chat: FunctionComponent = () => {
  const { t } = useTranslation("chat");
  const dispatch = useAppDispatch();
  const { activeChannels, channelsListStatus, channels } = useAppSelector((store) => store.chat, shallowEqual);
  // TRE-2076: na telefonie (< 768 px) lista kanałów i rozmowa to dwa ekrany, nie dwie kolumny.
  // Wyżej bez zmian — lista od `xl`, poniżej trenerka używa drawera i pływających okienek.
  const isPhone = useIsPhone();
  // `addChannelToList` dopisuje kanał na koniec kolejki. Na telefonie pokazujemy ten
  // ostatnio otwarty — po wyjściu z /chat menu (nie strzałką) poprzedni kanał zostaje
  // w kolejce i „Wyślij wiadomość" u innej klientki otwierałoby cudzą rozmowę.
  const channel = isPhone ? activeChannels.at(-1) : activeChannels[0];
  const showListOnMobile = isPhone && !channel;

  return (
    <div className="chat-height relative z-0 flex flex-1 overflow-hidden">
      <main
        className={`relative z-0 flex-1 overflow-y-hidden overscroll-none focus:outline-none xl:order-last ${
          showListOnMobile ? "hidden" : ""
        }`}
      >
        {channel ? (
          // Te same providery co przy pływających okienkach — ChatInput rzuca bez ChatUtilitiesProvider.
          <UploadFilesProvider key={channel.id} {...getUploadConfig(channel.id)}>
            <ChatUtilitiesProvider>
              <ChatMessageEditProvider>
                {isPhone ? (
                  <div className="flex h-full flex-col bg-white">
                    <div className="flex-shrink-0 border-b border-gray-200 bg-white px-2 py-1">
                      <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() =>
                          activeChannels.forEach((active) => dispatch(chatActions.removeChannelFromList(active.id)))
                        }
                      >
                        {t("channels")}
                      </Button>
                    </div>
                    <div className="min-h-0 flex-1">
                      <ChatWindow channel={channel} fullScreen />
                    </div>
                  </div>
                ) : (
                  <ChatWindow channel={channel} fullScreen />
                )}
              </ChatMessageEditProvider>
            </ChatUtilitiesProvider>
          </UploadFilesProvider>
        ) : (
          <div className="mt-24 xl:mt-36">
            <Empty description={t("selectChannel")} />
          </div>
        )}
      </main>
      <aside
        className={`flex-shrink-0 border-gray-200 bg-white xl:order-first xl:m-4 xl:flex xl:w-96 xl:flex-col xl:border-r xl:px-6 ${
          showListOnMobile ? "flex w-full flex-col px-3" : "hidden"
        }`}
      >
        <div className="py-4">
          <Typography.Title level={4}>{t("channels")}</Typography.Title>
        </div>
        <nav className="flex-1 overflow-y-auto " aria-label="Directory">
          <ChatChannelsList
            channels={channels}
            chatStatus={ChatStatus.Active}
            changeTab={() => null}
            status={channelsListStatus}
          />
        </nav>
      </aside>
    </div>
  );
};

export default Chat;
